import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Container, Spinner } from 'react-bootstrap';
import { FaCubes, FaDatabase, FaLink, FaSave } from 'react-icons/fa';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import AdminFormLayout from './AdminFormLayout';
import Navigation from './Navigation';
import {
    findOutletMenuModuleByPath,
    isRegisteredOutletMenuPath,
    outletMenuModuleCatalog,
} from './outletMenuModuleRegistry';

const ACTIVE_STATUS_VALUE = '1';
const INACTIVE_STATUS_VALUE = '0';

const normalizeModuleStatusValue = value => {
    const normalized = String(value ?? '').trim().toLowerCase();
    if (['active', '1', 'true', 'yes', 'on'].includes(normalized)) {
        return ACTIVE_STATUS_VALUE;
    }
    if (['inactive', '0', 'false', 'no', 'off'].includes(normalized)) {
        return INACTIVE_STATUS_VALUE;
    }
    return '';
};

function EditModule() {
    const { id } = useParams(); // Get the ID from the URL
    const navigate = useNavigate();
    const [moduleData, setModuleData] = useState({
        Modulename: '',
        Modulelink: '',
        Modulestatus: '',
        Filename: '',
        Tablename: '',
        Databasename: '',
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchModuleData = async () => {
            const token = localStorage.getItem('sessionToken');
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}getmodule`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ id: parseInt(id) }), // Ensure id is a number
                });

                const data = await response.json();
                if (data.error === "0") {
                    const selectedModule = findOutletMenuModuleByPath(data.results?.Modulelink);
                    setModuleData({
                        ...data.results,
                        Modulename: isRegisteredOutletMenuPath(data.results?.Modulelink)
                            ? selectedModule?.label || data.results?.Modulename || ''
                            : data.results?.Modulename || selectedModule?.label || '',
                        Modulelink: selectedModule?.path || data.results?.Modulelink || '',
                        Modulestatus: normalizeModuleStatusValue(data.results?.Modulestatus),
                    });
                } else {
                    setError('Failed to fetch module data');
                }
            } catch (err) {
                console.error(err);
                setError("An error occurred while fetching module data");
            } finally {
                setLoading(false);
            }
        };

        fetchModuleData();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setModuleData(prevData => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleMenuModuleChange = (value) => {
        const selectedModule = findOutletMenuModuleByPath(value);
        setModuleData(prevData => ({
            ...prevData,
            Modulelink: selectedModule?.path || value,
            Modulename: isRegisteredOutletMenuPath(value)
                ? selectedModule?.label || prevData.Modulename
                : prevData.Modulename,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('sessionToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}editmodule`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ ...moduleData, id: parseInt(id) }), // Include ID in payload
            });

            const data = await response.json();
            if (data.error === "0") {
                Swal.fire('Success!', 'Module updated successfully.', 'success');
                navigate('/modules'); // Redirect to dashboard after successful update
            } else {
                Swal.fire('Error!', data.message || 'Failed to update module.', 'error');
            }
        } catch (err) {
            console.error(err);
            Swal.fire('Error!', 'Failed to update module.', 'error');
        }
    };

    if (loading) {
        return (
            <Container className="mt-4 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return <Container className="mt-4 text-danger">{error}</Container>;
    }

    return (
        <div>
            <Navigation />
            <AdminFormLayout
                fluid={false}
                containerClassName="admin-page-shell mt-4"
                icon={FaCubes}
                eyebrow="Module maintenance"
                title="Edit Module"
                description="Perbarui pengaturan modul agar route, file, dan database tetap sinkron dengan source code."
                breadcrumbs={[
                    { label: 'Dashboard', to: '/dashboardadmin' },
                    { label: 'Module List', to: '/modules' },
                    { label: 'Edit Module' },
                ]}
                tips={[
                    'Pastikan perubahan route tidak memutus halaman yang sudah aktif dipakai admin.',
                    'Update mapping database dengan hati-hati untuk mencegah query salah sasaran.',
                    'Simpan hanya setelah semua field teknis sudah diverifikasi ulang.',
                ]}
            >
                <Form onSubmit={handleSubmit}>
                    <section className="form-section">
                        <div className="form-section__header">
                            <span className="form-section__icon">
                                <FaLink />
                            </span>
                            <div className="form-section__copy">
                                <h2 className="form-section__title">Module identity</h2>
                                <p className="form-section__description">
                                    Ubah nama modul, link, dan status tampil sesuai kebutuhan operasional saat ini.
                                </p>
                            </div>
                        </div>

                        <div className="form-grid">
                            <Form.Group controlId="formModuleName" className="field-stack span-6">
                                <Form.Label>Module Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="Modulename"
                                    value={moduleData.Modulename}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter module name"
                                />
                            </Form.Group>
                            <Form.Group controlId="formModuleLink" className="field-stack span-6">
                                <Form.Label>Module Link</Form.Label>
                                <Form.Control
                                    type="text"
                                    list="module-route-options"
                                    value={moduleData.Modulelink}
                                    onChange={event => handleMenuModuleChange(event.target.value)}
                                    placeholder="/route-module"
                                    required
                                />
                                <datalist id="module-route-options">
                                    {outletMenuModuleCatalog.map(module => (
                                        <option key={module.path} value={module.path}>
                                            {module.label}
                                        </option>
                                    ))}
                                </datalist>
                                <Form.Text>
                                    Module link bisa dipilih dari suggestion atau diketik manual jika route baru belum ada di daftar.
                                </Form.Text>
                            </Form.Group>
                            <Form.Group controlId="formModuleStatus" className="field-stack span-12">
                                <Form.Label>Module Status</Form.Label>
                                <Form.Select
                                    name="Modulestatus"
                                    value={moduleData.Modulestatus}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select status</option>
                                    <option value={ACTIVE_STATUS_VALUE}>Active</option>
                                    <option value={INACTIVE_STATUS_VALUE}>Inactive</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </section>

                    <section className="form-section">
                        <div className="form-section__header">
                            <span className="form-section__icon">
                                <FaDatabase />
                            </span>
                            <div className="form-section__copy">
                                <h2 className="form-section__title">Technical mapping</h2>
                                <p className="form-section__description">
                                    Pastikan file, tabel, dan database modul tetap sesuai dengan implementasi aktual.
                                </p>
                            </div>
                        </div>

                        <div className="form-grid">
                            <Form.Group controlId="formFileName" className="field-stack span-4">
                                <Form.Label>File Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="Filename"
                                    value={moduleData.Filename}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter file name"
                                />
                            </Form.Group>
                            <Form.Group controlId="formTableName" className="field-stack span-4">
                                <Form.Label>Table Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="Tablename"
                                    value={moduleData.Tablename}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter table name"
                                />
                            </Form.Group>
                            <Form.Group controlId="formDatabaseName" className="field-stack span-4">
                                <Form.Label>Database Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="Databasename"
                                    value={moduleData.Databasename}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter database name"
                                />
                            </Form.Group>
                        </div>
                    </section>

                    <div className="form-actions">
                        <Button variant="primary" type="submit">
                            <FaSave /> Save Changes
                        </Button>
                    </div>
                </Form>
            </AdminFormLayout>
        </div>
    );
}

export default EditModule;
